// src/components/Registration/steps/Step5Review.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import TIP_ICON_SRC from '../../../Images/tipEligible.png';
import { apiFetch } from '../../../api/client';

const PEACH = '#EBA477';
const DEFAULT_GMAP_ICON = 'https://maps.gstatic.com/mapfiles/place_api/icons_v2/generic_pin.png';

const Row = ({ label, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, marginBottom: 8 }}>
    <div style={{ opacity: 0.8 }}>{label}</div>
    <div>{children ?? '—'}</div>
  </div>
);

const Chip = ({ children }) => (
  <span
    style={{
      border: '1px solid rgba(255,255,255,0.25)',
      padding: '2px 8px',
      borderRadius: 999,
      background: 'rgba(255,255,255,0.06)',
      fontSize: 13,
      marginRight: 6,
      display: 'inline-block',
    }}
  >
    {children}
  </span>
);

function computeEinKey(einTaxId, idx) {
  const clean = String(einTaxId || '').trim();
  return clean ? `EIN:${clean}` : `IDX:${idx}`;
}

function mkMarket(key, extra = {}) {
  const mk = String(key || '').trim();
  if (!mk) return null;
  return {
    marketKey: mk,
    name: mk,
    marketType: extra.marketType || 'cbsa',
    attested: true,
    ...(extra.coverage ? { coverage: extra.coverage } : {}),
  };
}

const urlOk = (url) => /^https?:\/\/.+/i.test(url || '');

const ensureMarketEntry = (m) => {
  if (m && typeof m === 'object' && (m.marketKey || m.name)) {
    const key = m.marketKey || m.name;
    return key ? { marketKey: key, name: m.name || key, marketType: m.marketType, coverage: m.coverage } : null;
  }
  const key = String(m || '').trim();
  return key ? { marketKey: key, name: key } : null;
};

const flattenEinFormsToBusinesses = (einForms = []) => {
  const out = [];
  (Array.isArray(einForms) ? einForms : []).forEach((form, einIdx) => {
    if (!form) return;
    const brands = Array.isArray(form.brands) && form.brands.length ? form.brands : [];
    brands.forEach((brand, brandIdx) => {
      if (!brand) return;
      const key = String(brand.tempBusinessKey || form.tempBusinessKey || `${einIdx}-${brandIdx}`);
      const markets = (Array.isArray(brand.markets) ? brand.markets : [])
        .map(ensureMarketEntry)
        .filter(Boolean);
      const displayName =
        typeof brand?.brandName === 'string' && brand.brandName.trim()
          ? brand.brandName.trim()
          : typeof brand?.name === 'string'
          ? brand.name.trim()
          : '';
      out.push({
        legalEntityName: form.legalBusinessName || '',
        ein: form.einTaxId || '',
        ownershipType: form.ownershipType || '',
        primaryContact: form.primaryContact || { name: '', email: '', phone: '' },
        aliases: Array.isArray(form.aliases) ? form.aliases : [],
        dbaName: displayName,
        category: brand.businessCategory || '',
        baseContributionPct: Number(brand.baselineContribution || 2),
        tagline: brand.tagline || '',
        description: brand.description || '',
        logoUrl: brand.logoUrl || '',
        isOnlineOnly: !!brand.isOnlineOnly,
        primaryWebsiteUrl: brand.primaryWebsiteUrl || '',
        markets,
        tempBusinessKey: key,
        footprint: form.footprint || '',
      });
    });
  });
  return out;
};

/** Normalize Step 3 into a consistent shape (handles {step3View: {...}} or flat) */
function normalizeStep3(step3Data) {
  const s3 = step3Data?.step3View ? step3Data.step3View : (step3Data || {});
  const storeLocationsByKey = s3.storeLocationsByBusinessKey || s3.storeLocationsByEin || {};
  const marketsByKey = s3.marketsByBusinessKey || s3.marketsByEin || {};
  return {
    marketsByKey,
    marketsByEin: marketsByKey,
    storeLocationsByKey,
    storeLocationsByEin: storeLocationsByKey,
    storeLocations: Array.isArray(s3.storeLocations) ? s3.storeLocations : [],
    markets: Array.isArray(s3.markets) ? s3.markets : [],
    opsReviewQueue: !!s3.opsReviewQueue,
    duplicatePlaceIds: Array.isArray(s3.duplicatePlaceIds) ? s3.duplicatePlaceIds : [],
  };
}

/** Build a deduped markets[] from step2 + normalized step3 and from any location.marketKey */
function deriveAllMarkets(step2Data, s3norm) {
  const set = new Map();

// Per-brand declared markets
  Object.values(s3norm.marketsByKey || s3norm.marketsByEin || {}).forEach((arr) => {
    (Array.isArray(arr) ? arr : []).forEach((m) => {
      const mkt = mkMarket(m.marketKey || m.name, { marketType: m.marketType, coverage: m.coverage });
      if (mkt && !set.has(mkt.marketKey)) set.set(mkt.marketKey, mkt);
    });
  });

  // Flat step3 markets (if present)
  (Array.isArray(s3norm.markets) ? s3norm.markets : []).forEach((m) => {
    const mkt = mkMarket(m.marketKey || m.name, { marketType: m.marketType, coverage: m.coverage });
    if (mkt && !set.has(mkt.marketKey)) set.set(mkt.marketKey, mkt);
  });

  // From locations (grouped & flat)
  const addFromLoc = (loc) => {
    const mkt = mkMarket(loc.marketKey);
    if (mkt && !set.has(mkt.marketKey)) set.set(mkt.marketKey, mkt);
  };
  Object.values(s3norm.storeLocationsByEin || {}).forEach((arr) =>
    (Array.isArray(arr) ? arr : []).forEach(addFromLoc)
  );
  (Array.isArray(s3norm.storeLocations) ? s3norm.storeLocations : []).forEach(addFromLoc);

  // Step2 fallbacks
  const fallbackBusinesses =
    Array.isArray(step2Data?.businesses) && step2Data.businesses.length
      ? step2Data.businesses
      : Array.isArray(step2Data?.einForms)
        ? step2Data.einForms.flatMap((f) => {
            if (!Array.isArray(f?.brands)) return [];
            return f.brands.map((brand) => ({
              markets: brand.markets,
            }));
          })
        : [];
  (Array.isArray(fallbackBusinesses) ? fallbackBusinesses : []).forEach((b) => {
    const marketCandidates = Array.isArray(b?.markets) ? b.markets : [];
    marketCandidates.forEach((mk) => {
      const mkt = mkMarket(mk.marketKey || mk.name || mk);
      if (mkt && !set.has(mkt.marketKey)) set.set(mkt.marketKey, mkt);
    });
  });

  return Array.from(set.values());
}

export default function Step5Review({
  step1Data = {},
  step2Data = {},
  step3Data = {},
  step4Data = {},
  onPrevious,
  isFirstStep,
  onBindNext,                 // parent binds this to the Complete button
  sessionId,                  // parent passes sessionId
  skipAgreements = false,     // when true, don't require Step 4 T&C and hide that card
  accountId = null,
}) {
  const [submitError, setSubmitError] = useState('');

  // Normalize step 3 shape ONCE for all lookups
  const s3 = useMemo(() => normalizeStep3(step3Data), [step3Data]);

  // Slices for rendering
  const businesses = useMemo(() => {
    if (Array.isArray(step2Data?.businesses) && step2Data.businesses.length) {
      return step2Data.businesses;
    }
    return flattenEinFormsToBusinesses(step2Data?.einForms || []);
  }, [step2Data?.businesses, step2Data?.einForms]);

  const marketsByKey = useMemo(() => s3.marketsByKey || s3.marketsByEin || {}, [s3]);
  const locsByKey = useMemo(() => s3.storeLocationsByKey || s3.storeLocationsByEin || {}, [s3]);
  const allLocs = useMemo(() => s3.storeLocations, [s3]);

  // Cards per brand for display
  const reviewCards = useMemo(() => {
    return (Array.isArray(businesses) ? businesses : []).map((b, idx) => {
      const key = String(b.tempBusinessKey || computeEinKey(b.ein, idx));
      const isOnlineOnly = !!b.isOnlineOnly;

      const locations = isOnlineOnly
        ? []
        : (Array.isArray(locsByKey[key]) ? locsByKey[key] : null) ||
          allLocs.filter((l) => {
            const bizKey = String(l.businessKey || l.tempBusinessKey || computeEinKey(l.ein || l.einTaxId, idx));
            return bizKey === key;
          });

      const mkts = marketsByKey[key] || b.markets || [];
      const firstMarket =
        (Array.isArray(mkts) && mkts[0]?.marketKey) ||
        (Array.isArray(mkts) && mkts[0]?.name) ||
        '';

      const baselineNumeric = Number(b.baseContributionPct ?? b.baselineContribution ?? '');
      const baseline =
        !Number.isNaN(baselineNumeric) && baselineNumeric > 0 ? String(baselineNumeric) : '';

      const locationTipEligible = Array.isArray(locations) && locations.some((loc) => loc?.tipEligible);
      const tipEligible = b.tipEligible === true || locationTipEligible;

      return {
        key,
        idx,
        ein: b.ein || '',
        legalName: b.legalEntityName || '',
        brandName: b.dbaName || '',
        category: b.category || '',
        baseline,
        primaryContact: b.primaryContact || {},
        footprint: b.footprint || b.ownershipType || '',
        tipEligible,
        marketKey: firstMarket,
        locations: Array.isArray(locations) ? locations : [],
        isOnlineOnly,
        primaryWebsiteUrl: b.primaryWebsiteUrl || '',
      };
    });
  }, [businesses, locsByKey, allLocs, marketsByKey]);

  // Auth headers (support both cookies & bearer token)
  // --- API helper: PATCH a step (stable identity) ---
  const patchStep = useCallback(
    async (stepNumber, body) => {
      if (!sessionId) throw new Error('Missing session.');
      return apiFetch(
        `/brand/registration/${encodeURIComponent(sessionId)}/step/${stepNumber}`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
          orgContext: accountId
            ? { orgId: String(accountId), orgType: 'account' }
            : undefined,
        }
      );
    },
    [sessionId, accountId]
  );

  // --- Build and persist Step 3 per-brand before /complete ---
  const clickRef = useRef(async () => {});
  useEffect(() => {
    clickRef.current = async () => {
      setSubmitError('');

      // 0) Agreements pre-check (skip when skipAgreements=true)
      if (!skipAgreements) {
        const legal = step4Data?.legal || {};
        if (!legal?.acceptedTC) {
          setSubmitError('You must accept the Terms & Conditions (Step 4).');
          throw new Error('Terms not accepted.');
        }
      }

      // 1) Gather all distinct markets from everything (used mainly for validation below)
      const allMarkets = deriveAllMarkets(step2Data, s3);

      // 2) For each brand, determine its locations + markets and PATCH step 3
      const entries = Array.isArray(businesses) ? businesses : [];
      if (!entries.length) {
        setSubmitError('Missing business information (Step 2).');
        throw new Error('No business entries.');
      }

      // If any physical brands exist, ensure at least one market
      const physicalEntries = entries.filter((entry) => !entry.isOnlineOnly);
      if (physicalEntries.length > 0 && !allMarkets.length) {
        setSubmitError('At least one physical business requires market enrollment (Step 3).');
        throw new Error('At least one market enrollment is required for physical businesses before completing registration.');
      }

      for (let idx = 0; idx < entries.length; idx++) {
        const entry = entries[idx];
        const key = String(entry.tempBusinessKey || computeEinKey(entry.ein, idx));
        const isOnlineOnly = !!entry.isOnlineOnly;

        if (isOnlineOnly) {
          // Online-only: require a valid URL and clear step 3 artifacts
          if (!urlOk(entry.primaryWebsiteUrl)) {
            setSubmitError(`A valid website URL is required for online-only business "${entry.dbaName || entry.legalEntityName || `Business ${idx + 1}`}".`);
            throw new Error('Website URL is required for online-only businesses.');
          }
          await patchStep(3, {
            index: idx,
            businessKey: key,
            storeLocations: [],
            markets: [],
            opsReviewQueue: false,
            duplicatePlaceIds: [],
          });
          continue;
        }

        // --- Physical businesses only ---
        const locsForKey =
          (Array.isArray(locsByKey[key]) ? locsByKey[key] : null) ||
          allLocs.filter((l) => String(l.businessKey || l.tempBusinessKey || '') === key);

        // Require at least one location for each physical brand (to fail fast before /complete)
        const locCount = Array.isArray(locsForKey) ? locsForKey.length : 0;
        if (locCount === 0) {
          setSubmitError(`Please attach at least one location for "${entry.dbaName || entry.legalEntityName || `Business ${idx + 1}`}" in Step 3.`);
          throw new Error('At least one location is required for physical businesses before completing registration.');
        }

        const topMarketKey =
          (Array.isArray(marketsByKey[key]) && marketsByKey[key][0]?.marketKey) ||
          (Array.isArray(entry.markets) && entry.markets[0]?.marketKey) ||
          (Array.isArray(entry.markets) && entry.markets[0]?.name) ||
          allMarkets[0]?.marketKey ||
          '';

        const normalizedLocs = (Array.isArray(locsForKey) ? locsForKey : []).map((s) => {
          const latRaw = s.latitude ?? s.lat;
          const lngRaw = s.longitude ?? s.lng;
          const latitude = Number.isFinite(Number(latRaw)) ? Number(latRaw) : undefined;
          const longitude = Number.isFinite(Number(lngRaw)) ? Number(lngRaw) : undefined;

          // keep reopenDate as ISO if Date
          const reopen =
            s.reopenDate instanceof Date
              ? s.reopenDate.toISOString()
              : (typeof s.reopenDate === 'string' ? s.reopenDate : undefined);

          return {
            placeId: s.placeId || '',
            placeSource: s.placeSource || (s.placeId ? 'google' : 'manual'),
            name: s.name || '',
            addressText: s.addressText || '',
            phone: s.phone || '',
            website: s.website || '',
            hoursText: Array.isArray(s.hoursText) ? s.hoursText : [],
            logoUrl: s.logoUrl || '',
            managers: Array.isArray(s.managers) ? s.managers : [],
            contributionOverridePct:
              s.contributionOverridePct !== '' && s.contributionOverridePct != null
                ? Number(s.contributionOverridePct)
                : undefined,
            status: s.status === 'temp_excluded' ? 'temp_excluded' : 'active',
            exclusionReason: s.exclusionReason || '',
            reopenDate: reopen,
            marketKey: s.marketKey || topMarketKey,
            latitude,
            longitude,
            mcc: s.mcc || '',
            einTaxId: s.einTaxId || entry.ein || '',
            businessKey: key,
          };
        });

        const explicit = Array.isArray(marketsByKey[key]) ? marketsByKey[key] : [];
        const fromLocs = Array.from(
          new Set(normalizedLocs.map((l) => String(l.marketKey || '').trim()).filter(Boolean))
        ).map((mk) => mkMarket(mk));

        let effectiveMarkets = explicit.length ? explicit : fromLocs.length ? fromLocs : allMarkets;

        const coverageByMarket = (mk) => {
          const enrolled = normalizedLocs.filter((l) => (l.marketKey || '') === mk).length;
          const expected = normalizedLocs.length;
          const pct = expected > 0 ? Math.round((enrolled / expected) * 100) : (enrolled > 0 ? 100 : 0);
          return { discoveredCount: expected, enrolledCount: enrolled, coveragePct: pct };
        };
        effectiveMarkets = (effectiveMarkets || [])
          .map((m) => {
            const mkey = m.marketKey || m.name;
            if (!mkey) return null;
            return {
              marketKey: mkey,
              name: m.name || mkey,
              marketType: m.marketType || 'cbsa',
              attested: true,
              coverage: coverageByMarket(mkey),
            };
          })
          .filter(Boolean);

        await patchStep(3, {
          index: idx,
          businessKey: key,
          storeLocations: normalizedLocs,
          markets: effectiveMarkets,
          opsReviewQueue: !!s3.opsReviewQueue,
          duplicatePlaceIds: Array.isArray(s3.duplicatePlaceIds) ? s3.duplicatePlaceIds : [],
        });
      }

      // 3) (Optional) Persist agreements on step 4 if provided & not skipped
      if (!skipAgreements && step4Data?.legal) {
        try {
          await patchStep(4, { legal: step4Data.legal, acceptedTerms: !!step4Data.legal.acceptedTC });
        } catch (e) {
          // Don't block completion if this double-saves; backend tolerates already-saved step 4
          // But surface truly fatal errors
          if (!/Invalid|already/i.test(String(e?.message || ''))) throw e;
        }
      }

      return true;
    };
  }, [businesses, locsByKey, allLocs, marketsByKey, step2Data, s3, step4Data, patchStep, skipAgreements]);

  // Bind to parent so the shell can validate/persist review data before completing registration.
  useEffect(() => {
    if (!onBindNext) return;

    const handler = async () => {
      try {
        await clickRef.current();
      } catch (e) {
        console.error('Final review validation failed:', e);
        setSubmitError(e.message || 'Failed to validate final review.');
        throw e;
      }
    };

    onBindNext({ onClick: handler, disabled: false });
    return () => onBindNext({ onClick: null, disabled: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBindNext, clickRef]);

  /* ----------------------------- render ----------------------------- */
  const owner = step1Data || {};
  const legal = step4Data?.legal || {};

  return (
    <div style={{ color: 'white' }}>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Review & Confirm</h2>
      <p style={{ marginTop: 0, opacity: 0.9 }}>
        Please review your information. If everything looks good, press <b>Complete Registration</b>.
      </p>

      {submitError ? (
        <div
          style={{
            marginTop: 8,
            marginBottom: 8,
            borderRadius: 12,
            border: '1px solid rgba(255,0,0,0.35)',
            background: 'rgba(255,0,0,0.10)',
            padding: 12,
          }}
        >
          {submitError}
        </div>
      ) : null}

      {/* Owner / Account snapshot */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Account Owner / Admin</h3>
        <Row label="Full Name">{owner.fullName}</Row>
        <Row label="Email">{owner.email}</Row>
        <Row label="Phone">{owner.phone}</Row>
      </div>

      {/* Business Cards */}
      {(Array.isArray(reviewCards) ? reviewCards : []).map((c) => {
        const locs = Array.isArray(c.locations) ? c.locations : [];
        const isOnline = !!c.isOnlineOnly;

        return (
          <div key={c.key} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ marginTop: 0, marginBottom: 6 }}>
                  {c.brandName || c.legalName || `EIN ${c.ein || c.idx + 1}`}
                </h3>
                {c.tipEligible ? (
                  <img
                    src={TIP_ICON_SRC}
                    alt="Tip eligible"
                    title="Tip eligible"
                    style={{ width: 20, height: 20 }}
                  />
                ) : null}
              </div>
              <div style={{ alignSelf: 'flex-start', opacity: 0.9 }}>
                {c.ein ? <Chip>EIN {c.ein}</Chip> : <Chip>EIN pending</Chip>}
                {c.category ? <Chip>{c.category}</Chip> : null}
                {isOnline ? <Chip>Online Only</Chip> : c.footprint ? <Chip>{c.footprint}</Chip> : null}
                {!isOnline && c.marketKey ? <Chip>Market: {c.marketKey}</Chip> : null}
                {c.baseline ? <Chip>Baseline: {c.baseline}%</Chip> : null}
              </div>
            </div>

            <Row label="Legal Business Name">{c.legalName}</Row>
            <Row label="Public / Business Name">{c.brandName}</Row>

            {isOnline && (
              <Row label="Website URL">
                {c.primaryWebsiteUrl ? (
                  <a href={c.primaryWebsiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: PEACH }}>
                    {c.primaryWebsiteUrl}
                  </a>
                ) : '—'}
              </Row>
            )}

            <Row label="Primary Contact">
              {c.primaryContact?.name || c.primaryContact?.email || c.primaryContact?.phone ? (
                <div style={{ display: 'grid', gap: 2 }}>
                  <div>{c.primaryContact?.name || '—'}</div>
                  <div style={{ opacity: 0.9 }}>{c.primaryContact?.email || '—'}</div>
                  <div style={{ opacity: 0.9 }}>{c.primaryContact?.phone || '—'}</div>
                </div>
              ) : (
                '—'
              )}
            </Row>

            {!isOnline && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Locations ({locs.length})
                </div>
                {locs.length === 0 ? (
                  <div style={{ opacity: 0.85 }}>No locations attached to this business.</div>
                ) : (
                  <div style={tableWrap}>
                    {locs.map((l, i) => {
                      const iconUrl = l.iconMaskBaseUri ? `${l.iconMaskBaseUri}.png` : DEFAULT_GMAP_ICON;
                      const excluded = l.status === 'temp_excluded';
                      const reopen = l.reopenDate ? new Date(l.reopenDate) : null;
                      const reopenPretty = reopen && !isNaN(reopen) ? reopen.toLocaleDateString() : (l.reopenDate || '');

                      return (
                        <div key={`${l.placeId || 'manual'}-${i}`} style={rowStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: 6,
                                background: l.iconBackgroundColor || 'rgba(255,255,255,0.18)',
                                display: 'grid',
                                placeItems: 'center',
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.25)',
                              }}
                            >
                              <img
                                src={iconUrl}
                                alt=""
                                style={{ width: 18, height: 18, objectFit: 'contain' }}
                              />
                            </div>
                            <div style={{ fontWeight: 600 }}>
                              {l.name || '—'}
                              {excluded && (
                                <span style={{ marginLeft: 8, color: PEACH, fontWeight: 500 }}>
                                  • Temporarily Excluded
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={{ opacity: 0.9 }}>{l.addressText || '—'}</div>

                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {l.primaryType ? <Chip>{l.primaryType}</Chip> : null}
                            {l.businessStatus ? <Chip>Status: {l.businessStatus}</Chip> : null}
                            {l.marketKey ? <Chip>Market: {l.marketKey}</Chip> : null}
                          </div>

                          {excluded && (
                            <div style={{ marginTop: 6, opacity: 0.95 }}>
                              <span style={{ opacity: 0.85 }}>Reason:</span>{' '}
                              {l.exclusionReason || '—'}
                              {reopenPretty ? (
                                <>
                                  {' '}• <span style={{ opacity: 0.85 }}>Reopen:</span> {reopenPretty}
                                </>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Agreement snapshot: hidden when skipAgreements=true */}
      {!skipAgreements ? (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Agreement & Signature</h3>
          <Row label="Accepted Terms">{legal?.acceptedTC ? 'Yes' : 'No'}</Row>
          <Row label="Marketing Opt-In">{legal?.marketingOptIn ? 'Yes' : 'No'}</Row>
          <Row label="Signer Name">{legal?.signerName || '—'}</Row>
          <Row label="Signer Title">{legal?.signerTitle || '—'}</Row>
          <Row label="Signed On">
            {legal?.acceptedAt ? new Date(legal.acceptedAt).toLocaleString() : '—'}
          </Row>
        </div>
      ) : (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Agreement</h3>
          <div style={{ opacity: 0.9 }}>
            Using the existing agreement on file for this account. No additional acceptance required.
          </div>
        </div>
      )}

      {!isFirstStep && (
        <button
          type="button"
          onClick={onPrevious}
          style={{
            marginTop: 16,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.9)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Back
        </button>
      )}
    </div>
  );
}

/* ------- styles ------- */
const cardStyle = {
  marginTop: 16,
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(255,255,255,0.06)',
  padding: 16,
};

const tableWrap = {
  border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(255,255,255,0.04)',
  borderRadius: 14,
  overflow: 'hidden',
};

const rowStyle = {
  padding: '12px 14px',
  borderTop: '1px solid rgba(255,255,255,0.08)',
  display: 'grid',
  gap: 6,
};
